import type { ChartConfiguration } from 'chart.js'

import { Chart, registerables } from 'chart.js'
import { useEffect, useRef, useState } from 'react'
import 'chartjs-adapter-moment'

import http from '../../lib/http'
import { CircularProgress } from '../shared/NarusMaterial'

// Register Chart.js components
Chart.register(...registerables)

interface ChartData {
  id: string
  title: string
  height: string
  width: string
  config: ChartConfiguration
}

interface StatusData {
  charts: ChartData[]
}

interface ChartComponentProps {
  chartData: ChartData
}

function ChartComponent ({ chartData }: ChartComponentProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    // Destroy existing chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy()
    }

    // Create new chart
    chartRef.current = new Chart(canvasRef.current, chartData.config)

    // Cleanup function
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
    }
  }, [ chartData ])

  return (
    <div className='chart-container'>
      <h2
        style={{
          marginBottom: '1rem',
          fontSize: '1.2rem',
          fontWeight: 'bold',
          color: '#333'
        }}
      >
        {chartData.title}
      </h2>
      <div
        style={{
          position: 'relative',
          height: chartData.height,
          width: '100%',
          minHeight: '300px'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: '100%',
            height: 'auto'
          }}
        />
      </div>
    </div>
  )
}

export default function AdminStatus (): JSX.Element {
  const [ data, setData ] = useState<StatusData | null>(null)
  const [ loading, setLoading ] = useState(true)
  const [ error, setError ] = useState<string | null>(null)
  const [ lastUpdated, setLastUpdated ] = useState<Date | null>(null)
  const [ refreshing, setRefreshing ] = useState(false)

  const fetchData = async (isRefresh = false): Promise<void> => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const response = await http.getRequest('/admin/status')
      setData(response)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Failed to fetch status data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load status data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleRefresh = (): void => {
    void fetchData(true)
  }

  useEffect(() => {
    void fetchData()
  }, [])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Error Loading Status</h1>
        <p style={{ color: '#666', marginBottom: '1rem' }}>{error}</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              opacity: refreshing ? 0.6 : 1
            }}
          >
            {refreshing ? 'Retrying...' : 'Retry'}
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
        </div>
      </div>
    )
  }

  if (!data || !data.charts || data.charts.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Admin Status</h1>
        <p style={{ color: '#666' }}>No chart data available.</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}
      >
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
          Admin Status Dashboard
        </h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {lastUpdated && (
            <span style={{ fontSize: '0.875rem', color: '#666' }}>
              Last updated: {lastUpdated.toLocaleString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              opacity: refreshing ? 0.6 : 1,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {refreshing
              ? (
                <>
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid transparent',
                      borderTop: '2px solid currentColor',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                  Refreshing...
                </>
              )
              : (
                <>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      color: 'currentColor'
                    }}
                  >
                    ↻
                  </div>
                  Refresh
                </>
              )}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {data.charts.map((chartData) => (
          <div
            key={chartData.id}
            style={{
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '1.5rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <ChartComponent chartData={chartData} />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .chart-container h2 {
            font-size: 1rem !important;
          }
        }
      `}
      </style>
    </div>
  )
}
